/**
 * navigation-bar.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, HostListener } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { AuthService } from "../auth/auth.service";
import { UserService } from "../auth/identity/user.service";
import { ShortcutHelpModalComponent } from "./shortcut-help-modal.component";
import { BuildFlags } from "../configuration/build-flags";
import { DebugService } from "../debug/debug.service";
// import { DebugMessage, Message, UserMetadataMessage }
//   from "../server/messages/index";
import { MessagingService } from "../server/messaging.service";
import { SubjectService } from "../subjects/subject.service";
import { WatchlistService } from "../watchlist/watchlist.service";

import { overlayConfigFactory } from "angular2-modal";
import { BSModalContext, Modal } from "angular2-modal/plugins/bootstrap";
import { MockReceiverModalComponent }
  from "../debug/mock-receiver-modal.component";
import { RawSenderModalComponent } from "../debug/raw-sender-modal.component";
import { ModalHelpers } from "../support/utilities";
import { ExtensionPayload, ExtensionSignalRequest } from "app/server/messages/collaboration/extension-signal-request";
import { Rule } from "app/auth/permissions/rules";
import { ConfigurationService } from "app/configuration/configuration.service";
import { IframeModalComponent } from "./iframe-modal-dialog.component";
import { ModalService } from "./modal.service";
import { BrandingService } from "./branding.service";

/**
 * The {@link NavigationBarComponent} creates a persistent navigation bar
 * containing links to the major sections of the application.
 */
@Component({
  selector: "navbar",
  template: `
  <nav #responsiveMenu class="navbar navbar-default navbar-fixed-top">
  <div class="container-fluid">
    <div class="navbar-header">
      <!-- include mobile toggle here one day -->
      <a class="navbar-brand" [routerLink]="auth.authenticated ? '/home' : '/'">
        <img src="/zone/images/nav_banner.png">
      </a>
      <button
        type="button"
        title="Open menu"
        class="navbar-toggle collapsed"
        (click)="responsiveMenu.open = !responsiveMenu.open">
        <span class="glyphicon glyphicon-th-list"></span>
      </button>
    </div>

    <!-- this group would be hidden on small screens -->
    <div
      class="collapse navbar-collapse"
      [class.in]="responsiveMenu.open"
      (click)="responsiveMenu.open = false">

      <ul *ngIf="auth.authenticated" class="nav navbar-nav">

      <template [ngIf]="!algorithms.zoneIsAdmin">
      <!-- HOME -->
        <li *ngIf="!hideAll" routerLinkActive="active">
          <a routerLink="/home" preauthNewTabs class="capitalized">
            {{getLabel('home')}}
            <span *ngIf="watchlist.hasAlerts" class="badge alert-count">
              {{watchlist.alertCount}}
            </span>
          </a>
        </li>

        <!-- SEARCH -->
        <li
          *ngIf="features.search"
          title="Search" routerLinkActive="active">
          <a routerLink="/search" preauthNewTabs class="capitalized">
          {{getLabel('search')}}
          </a>
        </li>

        <!-- NEW INTAKE -->
        <li
          *ngIf="features.createSubject && algorithms?.createable?.length && (true || users.debugMode)"
          class="dropdown">
          <a
            *ngIf="algorithms.createable.length === 1"
            (click)="subjects.launchNew(algorithms.createable[0])"
            [title]="algorithms.createable[0].displayName"
            class="capitalized">
            {{getLabel('new', "New Intake")}}
          </a>
          <template [ngIf]="algorithms.createable.length > 1">
            <a (click)="$event.stopPropagation()" class="capitalized">
              {{getLabel('new', 'New Intake')}}
              <span class="caret"></span>
            </a>
            <ul class="dropdown-menu">
              <li
                *ngFor="let alg of algorithms.createable"
                routerLinkActive="active">
                <a (click)="subjects.launchNew(alg)">
                  {{alg.displayName}}
                </a>
              </li>
            </ul>
          </template>
        </li>

        <!-- AGGREGATION -->
        <li
          *ngIf="features.tools"
          title="Tools"
          routerLinkActive="active">
          <a routerLink="/tools" preauthNewTabs class="capitalized">
            {{getLabel('tools')}}
          </a>
        </li>

        <!-- MANAGEMENT -->
        <li
          *ngIf="features.management"
          title="Manage"
          routerLinkActive="active">
          <a routerLink="/manage" preauthNewTabs class="capitalized">
            {{getLabel('manage')}}
          </a>
        </li>
      </template>

        <!-- ADMIN PANEL -->
        <li
          *ngIf="algorithms.zoneIsAdmin"
          title="Admin Panel"
          routerLinkActive="active">
          <a routerLink="/admin" preauthNewTabs class="capitalized">
            {{getLabel('admin', 'Admin Panel')}}
          </a>
        </li>

        <!-- DEBUG -->
        <li
          *ngIf="users.debugMode"
          title="Debug menu"
          class="dropdown debug-only">
          <a (click)="$event.stopPropagation()">
            <span class="hidden-sm capitalized">
              {{getLabel('debug')}}
            </span>
            <span class="glyphicon glyphicon-wrench visible-sm-inline-block">
            </span>
            <span class="caret"></span>
          </a>
          <ul class="dropdown-menu">
            <li><a (click)="debugOpenSender()">
              Raw Sender
              <kbd class="pull-right">F2</kbd>
            </a></li>
            <li><a (click)="debugOpenMocker()">
              Mock Receiver
              <kbd class="pull-right">F3</kbd>
            </a></li>
            <li><a routerLink="sandbox">Sandbox Test Page</a></li>
            <li role="separator" class="divider"></li>
            <li>
              <a (click)="debugRemoveDeveloper()">
                Remove user dev status
              </a>
            </li>
            <li><a (click)="debugWSRestart()">Force websocket restart</a></li>
            <li>
              <a (click)="debugRecents()">
                Dump recent messages to console
              </a>
            </li>
            <li>
              <a (click)="debugServerBreak()">
                Trigger server breakpoint
              </a>
            </li>
            <li>
              <a (click)="subjects.recentSubjectsDialog()">
                Known Subjects
              </a>
            </li>
            <li>
              <a
                title="Debug action"
                (click)="debugMiscTest()">
                <span class="glyphicon glyphicon-star"></span> Curr. Test Rig
              </a>
            </li>
          </ul>
        </li>

        <li *ngIf="!hideAll" class="hidden-xs">
          <a
            title="Open this in a new tab"
            (click)="openNewAuthedTab($event)">
            <span class="glyphicon glyphicon-new-window"></span>
          </a>
        </li>
      </ul>
      <!-- end of main (left-side) links -->


      <!-- Login/logout links, which are pushed to the right -->
      <ul class="nav navbar-nav navbar-right">
        <li
          *ngIf="algorithms.issueReporting && !hideAll"
          title="Report issues"
          [class.dropdown]="algorithms.issueReporters.length > 1">
          <a
            *ngIf="algorithms.issueReporters.length === 1"
            (click)="subjects.launchNew(algorithms.issueReporters[0])">
            <span class="hidden-sm">Report Issues</span>
            <span class="glyphicon glyphicon-fire visible-sm-inline-block">
            </span>
          </a>
          <template [ngIf]="algorithms.issueReporters.length > 1">
            <a>
              <span class="hidden-sm">Report Issues</span>
              <span class="glyphicon glyphicon-fire visible-sm-inline-block">
              </span>
              <span class="caret"></span>
            </a>
            <ul class="dropdown-menu">
              <li
                *ngFor="let ir of algorithms.issueReporters"
                routerLinkActive="active">
                <a (click)="subjects.launchNew(ir)">
                  {{ir.displayName}}
                </a>
              </li>
            </ul>
          </template>
        </li>
        <li *ngIf="auth.authenticated && !hideAll" routerLinkActive="active">
          <a routerLink="/settings">
            <span class="hidden-sm capitalized">{{getLabel('settings')}}</span>
            <span class="glyphicon glyphicon-cog visible-sm-inline-block">
            </span>
          </a>
        </li>
        <li *ngIf="config.client.type === 'guest'">
          <a (click)="showContactPage()">
            <!-- <span class="glyphicon glyphicon-info-sign"></span> -->
            Help / FAQ
          </a>
        </li>
        <li *ngIf="!auth.authenticated" title="Login" routerLinkActive="active">
          <a routerLink="/login">
            <span class="hidden-sm">Login</span>
            <span class="glyphicon glyphicon-log-in visible-sm-inline-block">
            </span>
          </a>
        </li>
        <li *ngIf="auth.authenticated" title="Logout" routerLinkActive="active">
          <a routerLink="/logout">
            <span class="hidden-sm">Logout</span>
            <span class="glyphicon glyphicon-log-out visible-sm-inline-block">
            </span>
          </a>
        </li>
      </ul>
    </div><!-- /.navbar-collapse -->
  </div><!-- /.container-fluid -->
</nav>
  `,
  styles: [
    ".alert-count {background-color: #f0ad4e;}",
    ".navbar-default .navbar-nav>li.text-warning>a {color: #8a6d3b;}",
    ".nav>li:hover {background: rgb(253,253,253);}",
    `
    .dropdown:hover .dropdown-menu {display: block;}
    `
  ]
})
export class NavigationBarComponent
{
  public features : {[k : string] : boolean} = {
    search: false,
    createSubject: false,
    tools: false,
    management: false
  };
  public hideAll : boolean = false;
  private clearFeatures () : void
  {
    this.features.search = false;
  }
  private extractFeatures () : void
  {
    if (this.config.client.type === "guest") {
      this.features = {
        search: false,
        createSubject: this.users.self.hasRule(Rule.POPULATE_SUBJECT),
        tools: false,
        management: false
      };
      this.hideAll = true;
    } else {
      this.features = {
        search: this.users.self.hasRule(Rule.SEARCH_SUBJECT),
        createSubject: this.users.self.hasRule(Rule.POPULATE_SUBJECT),
        tools: this.users.self.isAggregationUser(),
        management: this.users.self.isManagementUser()
      };
    }
  }

  private getLabel (section : string, def? : string) : string {
    const k = `nav_${section}`;
    if (k in this.branding.strings) {
      return this.branding.strings[k];
    }
    if (def) {
      return def;
    } else {
      return section;
    }
  }


  /**
   * Construct a new {@link NavigationBarComponent}.
   *
   */
  public constructor (
    protected auth : AuthService,
    protected algorithms : AlgorithmService,
    protected subjects : SubjectService,
    protected messaging : MessagingService,
    private modal : Modal,
    protected modals : ModalService,
    protected debug : DebugService,
    protected watchlist : WatchlistService,
    protected users : UserService,
    public router : Router,
    protected config : ConfigurationService,
    protected branding : BrandingService
  )
  {
    // TODO: This should live elsewhere
    if (this.algorithms.zoneIsAdmin)
    {
      this.auth.redirectTo = ["admin"];
    }

    this.users.ready.subscribe((ready : boolean) => {
      if (ready)
      {
        this.extractFeatures();
      }
      else
      {
        this.clearFeatures();
      }
    })
  }

  public helpOpen : boolean = false;
  @HostListener("document:keyup.f1", ["$event"])
  @HostListener("document:keyup.control./", ["$event"])
  public openHelpModal (ev : KeyboardEvent) : void
  {
    if (!this.helpOpen)
    {
      this.helpOpen = true;
      ModalHelpers.observeCustom(
        this.modal,
        ShortcutHelpModalComponent,
        {}
      ).subscribe(() => {
        this.helpOpen = false;
      });
    }
    ev.preventDefault();
  }

  @HostListener("document:keyup.f2", ["$event"])
  private debugOpenSender (ev : KeyboardEvent) : void
  {
    if (this.users.debugMode)
    {
      this.modal.open(
        RawSenderModalComponent,
        overlayConfigFactory({size: "lg"}, BSModalContext)
      );
      ev.preventDefault();
    }
  }

  @HostListener("document:keyup.f3", ["$event"])
  private debugOpenMocker (ev : KeyboardEvent) : void
  {
    if (this.users.debugMode)
    {
      this.modal.open(
        MockReceiverModalComponent,
        overlayConfigFactory({size: "lg"}, BSModalContext)
      );
      ev.preventDefault();
    }
  }

  @HostListener("document:keyup.f4", ["$event"])
  private toggleHideAll (ev : KeyboardEvent) : void
  {
    if (this.hideAll) {
      this.hideAll = false;
      this.features.tools = true;
    }
  }

  // tslint:disable-next-line:no-unused-variable
  private debugWSRestart () : void
  {
    this.messaging.restart();
  }

  // tslint:disable-next-line:no-unused-variable
  private debugRecents () : void
  {
    console.table(this.messaging.recentLog.list());
  }

  // tslint:disable-next-line:no-unused-variable
  private debugServerBreak () : void
  {
    this.debug.breakpoint();
  }

  public debugRemoveDeveloper () : void
  {
    this.users.undebug();
  }

  public debugMiscTest () : void
  {
    const payload : ExtensionPayload = {
      name: "debug",
      version: [0, 0, 0]
    };
    this.messaging.send(new ExtensionSignalRequest(payload)).subscribe();
  }

  // tslint:disable-next-line:no-unused-variable
  public openNewAuthedTab (event : MouseEvent) : boolean
  {
    event.preventDefault();
    this.auth.openPreauthedTab();
    return false;
  }

  @HostListener("document:keydown.control.h")
  @HostListener("document:keydown.meta.h")
  public navigateHome () : void
  {
    this.router.navigate(["/home"]);
  }

  @HostListener("document:keydown.control.i")
  @HostListener("document:keydown.meta.i")
  public newIntake () : void
  {
    this.router.navigate(["/subject/new"]);
  }

  @HostListener("document:keydown.control.b")
  @HostListener("document:keydown.meta.b")
  public recentsDialog () : void
  {
    this.subjects.recentSubjectsDialog();
  }

  public showContactPage (event : KeyboardEvent) : void
  {
    this.modals.display(
      IframeModalComponent,
      {url: "/zone/documents/faq.html", title: "Frequently Asked Questions", size: 'lg'}
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/navigation-bar.component.ts